import * as Linking from 'expo-linking';
import * as storage from '../../../utils/storage';
import { captureException } from '../../../utils/sentry/sentry';
import Button from '../../../components/Button/Button';
import Loading from '../../../components/Loading/Loading';
import { AppState, Keyboard, KeyboardAvoidingView, ScrollView, TouchableOpacity,TouchableWithoutFeedback, View } from 'react-native';
import { COLORS, ERROR_COLOR } from '../../../config/designSystem/Colors';
import { LOGIN } from '../../../state/mutations/User';
import { LoginStackParamList } from '../../../types/navigation';
import { MARGIN_BOTTOM_BUTTON } from './_layout';
import { MARGIN_HORIZONTAL_TEXT_INPUT, MAX_WIDTH_TEXT_INPUT, TextInput2 } from '../../../components/TextInput/TextInput2';
import { ParagraphMBold, ParagraphSRegular } from '../../../config/designSystem/typography';
import { SPACING } from '../../../config/designSystem/Spacing';
import { client } from '../../../config/apollo/client';
import { concatPath } from '../../../utils/links/concatPath';
import { dashboardKey, dashboardStackKey, forgetPasswordKey, privateKey } from '../../../constants/routeKeys';
import { elliRedirectStringForStore, elliTokenStringForStore } from '../../../constants/storageKeys';
import { getLabelByPath } from '../../../utils/labelIter';
import { isWeb } from '../../../config/designSystem/Layout';
import { logAmplitude, setProperty } from '../../../utils/analytics/analytics';
import { observer } from 'mobx-react';
import { router, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { useAvailableDimensions } from '../../../hooks/ui/useAvailableDimensions';
import { useEffect, useRef, useState } from 'react';
import { useManageRedirect } from '../../../hooks/authentification/useManageRedirect';
import { useManageToken } from '../../../hooks/authentification/useManageToken';
import { usePreventScreenCaptureWhileFocused } from '../../../hooks/usePreventScreenCaptureWhileFocused';
import { useStores } from '../../../hooks/useStores';
import { useVerticalOffsetHeight } from '../../../hooks/ui/useVerticalOffsetHeight';




type LoginProps = LoginStackParamList['Login'];


const Login: React.FunctionComponent<LoginProps> = observer(() => {
  const params = useLocalSearchParams();

  const [loginStatus, setLoginStatus] =useState('unset');
  const [errorMessage, setErrorMessage] =useState('');

  const [emailValue, onChangeTextEmail] = useState('');
  const [passwordValue, onChangeTextPassword] = useState('');
  const [afterRegister, setAfterRegister] = useState(false);
  const [loadingNextPage, setLoadingNextPage] = useState(false);

  const [forceHiding, setForceHiding] = useState(false);



  const { width, height } = useAvailableDimensions();
  const passwordInputRef = useRef(null);
  const emailInputRef = useRef(null);

  const rootStore = useStores();
  usePreventScreenCaptureWhileFocused();

  const allLabels = rootStore?.language.translate;
  const labels = allLabels.MOBILE.LOGIN;

  const appState = useRef(AppState.currentState);
  const [appStateVisible, setAppStateVisible] = useState(appState.current);

  const MARGIN_HORIZONTAL = 25;
  const WIDTH = Math.max(100, Math.min(800, width)) - 2 * MARGIN_HORIZONTAL;

  // const headerHeight = useHeaderHeight(); //create many rerenders

  const verticalOffSetHeight = useVerticalOffsetHeight();
  const {redirect} = useManageRedirect();


  const onPressForgot = () => {
    router.navigate(concatPath([forgetPasswordKey]));
  };

  const goToMainTab = () => {
    router.navigate(concatPath([privateKey, dashboardStackKey, dashboardKey],2));
  };







  useEffect(() => {
    const subscription = AppState.addEventListener('change', nextAppState => {
      appState.current = nextAppState;
      setAppStateVisible(appState.current);
    });
    return () => {
      subscription.remove();
    };


  }, []);



  useEffect(() => {
    setForceHiding(appStateVisible !== 'active');
  }, [appStateVisible]);


  useFocusEffect(() => {
    if (!afterRegister) {
      if (params && params.email) {
        onChangeTextEmail(params.email);
        setAfterRegister(true);
      }
      if (params && params.password) {
        onChangeTextPassword(params.password);
        setAfterRegister(true);
      }
    }

  });

  useEffect(() => {
    setLoginStatus('unset');
    setErrorMessage('');
  }, [emailValue, passwordValue]);




  const afterLoginCallback = () => {
    setProperty({ hasConnected: true });
    logAmplitude('Login Succeeded');
    setLoginStatus('done');
    redirect(false);
  };

  const manageToken = useManageToken(afterLoginCallback, false, false);


  // manageToken is async and is intentionally NOT awaited -- the spinner is meant to
  // stay up while it drives the auth flow and navigates away. But an unawaited promise
  // cannot reject into the enclosing try/catch, and with the `finally` removed nothing
  // would clear the flag: decodeToken() throws on a malformed payload, and register()
  // awaits the profile fetch and setLanguage BEFORE its callback, so a network drop
  // right after auth leaves the user on a spinner that never resolves. Catch explicitly.
  const startSession = (token: string) => {
    manageToken(token).catch((err: unknown) => {
      setLoadingNextPage(false);
      setLoginStatus('error');
      setErrorMessage(labels?.UNKNOWN_ERROR);
      captureException(err);
    });
  };

  const login = async (email: string, password: string): Promise<void> => {
    setLoginStatus('pending');
    if (email.length < 5) {
      setLoginStatus('notCompletedField');
    } else {
      setLoadingNextPage(true);
      try {
        // Check for Elli token and redirect URL from storage
        const elliToken = await storage.loadString(elliTokenStringForStore);
        const elliRedirectUrl = await storage.loadString(elliRedirectStringForStore);

        // Only use elliToken if both token and redirect URL are present
        const hasElliFlow = elliToken && elliRedirectUrl;

        const {data, errors} = await client.mutate({
          mutation: LOGIN,
          variables: { email: email.toLowerCase(), password, elliToken: hasElliFlow ? elliToken : undefined },
          fetchPolicy:'no-cache'
        });
        if(data) {
          const loginResponse = (data as any).login;

          if (hasElliFlow) {
            storage.remove(elliTokenStringForStore);
            storage.remove(elliRedirectStringForStore);
            const { token } = JSON.parse(loginResponse);
            Linking.openURL(elliRedirectUrl);
            startSession(token);
          } else {
            // Clean up any stale elli data
            storage.remove(elliTokenStringForStore);
            storage.remove(elliRedirectStringForStore);
            startSession(loginResponse);
          }
        } else if(errors) {
          setLoadingNextPage(false);
          setLoginStatus('error');
          if ((errors as any)?.graphQLErrors[0]?.translationKey && (errors as any)?.graphQLErrors[0]?.code === 403) {
            const val = getLabelByPath(allLabels, (errors as any)?.graphQLErrors[0]?.translationKey);
            if(val) setErrorMessage(val);
          } else {
            setErrorMessage(labels.UNKNOWN_ERROR);
          }

        } else {
          // Neither data nor errors: nothing will navigate, so do not leave the user
          // staring at a spinner that can never resolve.
          setLoadingNextPage(false);
          setLoginStatus('error');
          setErrorMessage(labels.UNKNOWN_ERROR);
        }

      } catch(err) {
        setLoadingNextPage(false);
        setLoginStatus('error');
        if ((err as any)?.graphQLErrors[0]?.translationKey && (err as any)?.graphQLErrors[0]?.code === 403) {
          const val = getLabelByPath(allLabels, (err as any)?.graphQLErrors[0]?.translationKey);
          if(val) setErrorMessage(val);
        } else {
          setErrorMessage(labels.UNKNOWN_ERROR);
        }
        setLoginStatus('error');
        logAmplitude('Login Failed');
      }
      // NOTE: deliberately no `finally { setLoadingNextPage(false) }`.
      //
      // On success the spinner must STAY up. manageToken() only starts the auth flow --
      // fetching the profile and navigating happen afterwards, so clearing the flag here
      // dropped the user back onto the login form, apparently idle, until the navigation
      // landed a moment later ("nothing happens, then suddenly we're in the app"). This
      // screen is about to be replaced, so leaving the spinner up is the honest state.
      // Every failure path below clears it explicitly instead.
    }
  };

  const onPressLogin = () => {
    login(emailValue, passwordValue);
  };

  const handleNext = () => {
    if (passwordInputRef !== null) {
      passwordInputRef.current.focus();
    }
  };






  const EndOfLogin = () => {
    return (
      // Deliberately NOT position:'absolute'. Pinning this to the bottom of a
      // container that shrinks when the keyboard opens made it ride up over the
      // password field. In normal flow, below a flex:1 ScrollView, it still sits at
      // the bottom when there is room and can never overlap the fields.
      <View style={
        [{ marginTop: 10, alignItems: 'center', alignSelf:'center', width: WIDTH, justifyContent:'center',
          marginHorizontal:MARGIN_HORIZONTAL},!isWeb && {marginBottom: MARGIN_BOTTOM_BUTTON }]}>
        {/* Reserve the error line's height so the button does not move.
            A useEffect resets loginStatus to 'unset' on EVERY keystroke, so an error
            message showing above the button vanished as soon as the user started
            correcting the field -- this container lost a line and the button visibly
            jumped. minHeight keeps the row occupying space whether or not it has text. */}
        <View style={{minHeight: SPACING['sp-20'], justifyContent: 'center'}}>
          {errorMessageFn()}
        </View>
        <Button width={WIDTH} onPress={onPressLogin} label={labels?.LOGIN_BUTTON ? labels?.LOGIN_BUTTON : 'J\'ai termine'} />
      </View>);
  };

  if (loadingNextPage) {
    return (
      <View style={[{ flex:1}, isWeb && {marginTop: 200}]}>
        <Loading withLabel={false}/>
      </View>
    );
  }

  const errorMessageFn = () => {
    return(
      <>
        {loginStatus === 'error' && (
          <ParagraphSRegular color={ERROR_COLOR}>
            {errorMessage !== ''
              ? errorMessage
              : labels?.ERROR_MESSAGE
                ? labels?.ERROR_MESSAGE
                : 'Could not login. Please try again'}
          </ParagraphSRegular>

        )}
        {loginStatus === 'notCompletedField' && (
          <ParagraphSRegular color={ERROR_COLOR}>
            {labels?.ERROR_COMPLETE_FIELDS ? labels?.ERROR_COMPLETE_FIELDS : 'Please complete the fields above.'}
          </ParagraphSRegular>
        )}
      </>);
  };

  const FieldsToDisplay = () => {
    return(<View style={{ alignItems: 'center',flex:1, justifyContent: 'center', maxWidth:MAX_WIDTH_TEXT_INPUT, alignSelf:'center' }}>
      <TextInput2
        label={labels?.EMAIL_PLACEHOLDER}
        value={emailValue}
        textContentType='emailAddress'
        returnKeyType='next'
        secured={false}
        image='envelope'
        reference={emailInputRef}
        // autoCompleteType={passwordInputRef}
        onChangeText={(text: string) => onChangeTextEmail(text)}
        onSubmitEditing={handleNext}
        autoFocus={true}
      />
      <TextInput2
        label={labels?.PASSWORD_PLACEHOLDER}
        value={passwordValue}
        textContentType='password'
        returnKeyType='done'
        secured={true}
        image='lock'
        reference={passwordInputRef}
        onChangeText={text => onChangeTextPassword(text)}
        onSubmitEditing={onPressLogin}
        marginTop={30}
        autoComplete='password'
        forceHiding={forceHiding}
      />
      <TouchableOpacity
        style={{
          marginHorizontal: MARGIN_HORIZONTAL_TEXT_INPUT + 30,
          marginTop:10,
          alignSelf:'flex-end',
        }}

        onPress={onPressForgot}
      >
        <ParagraphMBold color={COLORS.brand[500] }>
          {labels?.FORGOT_PASSWORD ? labels?.FORGOT_PASSWORD : 'Forgot password?'}
        </ParagraphMBold>
      </TouchableOpacity>
    </View>);
  };

  const mainPartWeb = () => {
    return(
      <View style={{backgroundColor:'white',flex:1}}>
        <ScrollView  >
          {FieldsToDisplay()}
          <EndOfLogin />
        </ScrollView>
      </View>);

  };



  const mainPart = () => {
    return(  <KeyboardAvoidingView behavior='padding'
      keyboardVerticalOffset={verticalOffSetHeight}
      style={{flex:1, backgroundColor:'white'}}
    >
      <TouchableWithoutFeedback
        accessible={false}
        onPress={() => Keyboard.dismiss()}
        style={{flex:1}}>
        <View style={{flex:1}}>
          <ScrollView
            style={{flex:1}}
            keyboardShouldPersistTaps="handled"
          >
            <View  style={{flex:1}}>
              {FieldsToDisplay()}

            </View>

          </ScrollView>

          <EndOfLogin />

        </View>

      </TouchableWithoutFeedback>
    </KeyboardAvoidingView>);
  };




  return (
    <>
      {isWeb ? mainPartWeb() : mainPart()}
    </>

  );});

export default Login;
